import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Flip } from "gsap/Flip";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEventHandler,
  type FocusEventHandler,
  type KeyboardEventHandler,
  type ReactNode,
  type RefObject,
} from "react";
import {
  TextArea,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";

import addIcon from "@/assets/figma/chat-add/add.svg";
import micIcon from "@/assets/figma/chat-add/mic.svg";
import sendActiveIcon from "@/assets/figma/chat-add/send-active.svg";
import sendDisabledIcon from "@/assets/figma/chat-add/send-disabled.svg";
import sendFocusedIcon from "@/assets/figma/chat-add/send-focused.svg";
import recordingIcon from "@/assets/figma/chat-add/recording.svg";
import recordingStopIcon from "@/assets/figma/chat-add/recording-stop.svg";
import stopIcon from "@/assets/figma/chat-add/stop.svg";
import { ChatIconButton } from "@/shared/ui/ChatIconButton";
import { cn } from "@/shared/lib/cn";
import { mergeSpeechTranscript } from "@/shared/lib/browserSpeechInput";
import { useBrowserSpeechInput } from "@/shared/lib/useBrowserSpeechInput";
import type { ChatAddInputState } from "@/shared/ui/chatAddInputState";
import type { MerryChatExtensionViewModel } from "@/shared/ui/chatExtensionTypes";
import { ChatInputError } from "@/shared/ui/chatInputError";
import { MerryChatExtensionBar } from "@/shared/ui/MerryChatExtensionBar";

gsap.registerPlugin(Flip, useGSAP);

const EMPTY_CHAT_EXTENSIONS: ReadonlyArray<MerryChatExtensionViewModel> = [];

export interface ChatAddInputProps {
  presentation?: "default" | "figma";
  className?: string;
  defaultValue?: string;
  extensions?: ReadonlyArray<MerryChatExtensionViewModel>;
  focusRequestKey?: number;
  isAddMenuOpen?: boolean;
  isExtensionMoreOpen?: boolean;
  isProcessing?: boolean;
  isSendDisabled?: boolean;
  onAdd?: AriaButtonProps["onPress"];
  onConfigureExtension?: (extensionId: string) => void;
  onExtensionMore?: (
    hiddenExtensions: ReadonlyArray<MerryChatExtensionViewModel>,
  ) => void;
  onMicrophone?: AriaButtonProps["onPress"];
  onRemoveExtension?: (extensionId: string) => void;
  onSend?: (message: string) => Promise<void> | void;
  speechScopeKey?: string;
  onStop?: () => void;
}

const figmaNodeIds: Record<ChatAddInputState, string> = {
  未输入: "435:57965",
  聚焦: "435:57972",
  进行中: "435:57979",
  单行输入: "435:57986",
  多行输入: "435:57993",
};

const layoutMotion = {
  duration: 0.48,
  ease: "power3.inOut",
} as const;

function getLayoutTargets(root: HTMLDivElement) {
  return [
    root,
    ...root.querySelectorAll<HTMLElement>("[data-chat-layout-item]"),
  ];
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

interface AddButtonProps {
  className: string;
  isOpen?: boolean;
  isProcessing: boolean;
  onAdd?: ChatAddInputProps["onAdd"];
}

function AddButton({ className, isOpen, isProcessing, onAdd }: AddButtonProps) {
  return (
    <ChatIconButton
      ariaExpanded={isOpen}
      ariaHasPopup="dialog"
      className={className}
      icon={addIcon}
      iconFrameClassName="inset-[8.34%_8.33%_8.33%_8.33%]"
      isDisabled={isProcessing || !onAdd}
      label="添加内容"
      onPress={onAdd}
    />
  );
}

interface MicrophoneButtonProps {
  presentation?: "default" | "figma";
  className: string;
  isProcessing: boolean;
  isListening?: boolean;
  onMicrophone?: ChatAddInputProps["onMicrophone"];
}

function MicrophoneButton({
  presentation,
  className,
  isProcessing,
  isListening,
  onMicrophone,
}: MicrophoneButtonProps) {
  return (
    <ChatIconButton
      className={className}
      icon={presentation === "figma" && isListening ? recordingIcon : micIcon}
      hoverIcon={presentation === "figma" && isListening ? recordingStopIcon : undefined}
      iconFrameClassName={presentation === "figma" && isListening ? "inset-0" : "inset-[4.17%_12.73%]"}
      isDisabled={isProcessing || !onMicrophone}
      label={isListening ? "停止语音输入" : "语音输入"}
      onPress={onMicrophone}
    />
  );
}

interface SubmitButtonProps {
  className: string;
  isSendDisabled: boolean;
  onSend: AriaButtonProps["onPress"];
  onStop: AriaButtonProps["onPress"];
  state: ChatAddInputState;
}

function SubmitButton({
  className,
  isSendDisabled,
  onSend,
  onStop,
  state,
}: SubmitButtonProps) {
  if (state === "进行中") {
    return (
      <ChatIconButton
        className={className}
        hasHoverMotion
        icon={stopIcon}
        iconFrameClassName="inset-[12.5%]"
        label="停止"
        onPress={onStop}
        tone="theme"
      />
    );
  }

  const hasContent =
    !isSendDisabled && (state === "单行输入" || state === "多行输入");
  const isFocused = state === "聚焦";

  return (
    <ChatIconButton
      className={className}
      hasHoverMotion
      icon={
        hasContent
          ? sendActiveIcon
          : isFocused
            ? sendFocusedIcon
            : sendDisabledIcon
      }
      iconFrameClassName="inset-[8.33%_4.16%_8.37%_4.17%]"
      isDisabled={!hasContent}
      label="发送"
      onPress={onSend}
      tone={hasContent ? "theme" : isFocused ? "focus" : "muted"}
    />
  );
}

interface ChatTextInputProps {
  extensionBar?: ReactNode;
  extensionLayout: "expanded" | "inline" | "none";
  inputRef: RefObject<HTMLTextAreaElement | null>;
  isMultiline: boolean;
  isProcessing: boolean;
  onBlur: FocusEventHandler<HTMLTextAreaElement>;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  onFocus: FocusEventHandler<HTMLTextAreaElement>;
  onKeyDown: KeyboardEventHandler<HTMLTextAreaElement>;
  state: ChatAddInputState;
  value: string;
}

function ChatTextInput({
  extensionBar,
  extensionLayout,
  inputRef,
  isMultiline,
  isProcessing,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  state,
  value,
}: ChatTextInputProps) {
  const showsEmptyCaret =
    value.length === 0 && (state === "聚焦" || state === "进行中");

  return (
    <div
      className={cn(
        "merry-chat-input__content relative z-10 flex min-w-0 items-center overflow-clip",
        extensionLayout === "expanded"
          ? "col-[1/-1] row-start-2 w-full p-[6px]"
          : isMultiline
            ? "col-[1/-1] row-start-1 w-full p-[6px]"
            : "col-start-3 row-start-1 py-[7px]",
        extensionLayout === "inline" &&
          "merry-chat-input__content--with-inline-extensions gap-3",
      )}
      data-chat-layout-item=""
    >
      {extensionLayout === "inline" ? extensionBar : null}
      <TextArea
        aria-label="向 Merry 提问"
        autoComplete="off"
        className={cn(
          "m-0 block w-full resize-none appearance-none overflow-hidden border-0 bg-transparent p-0",
          "font-[family-name:var(--merry-chat-font)] text-[16px] font-[350] leading-[22px] tracking-[0.5px] text-[var(--merry-chat-text-primary)] outline-none",
          "placeholder:text-[var(--merry-chat-text-disabled)] placeholder:opacity-100",
          isMultiline || extensionLayout === "expanded"
            ? "h-[44px]"
            : "h-[22px]",
          showsEmptyCaret
            ? "caret-transparent"
            : "caret-[var(--merry-chat-theme)]",
        )}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder="有问题，交给 Merry 办"
        readOnly={isProcessing}
        ref={inputRef}
        rows={1}
        spellCheck={false}
        value={value}
      />
      {showsEmptyCaret ? (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1/2 h-[18px] w-[2px] -translate-y-1/2 rounded-[2px] bg-[var(--merry-chat-theme)]"
        />
      ) : null}
    </div>
  );
}

export function ChatAddInput({
  className,
  defaultValue = "",
  extensions = EMPTY_CHAT_EXTENSIONS,
  focusRequestKey = 0,
  isAddMenuOpen,
  isExtensionMoreOpen,
  isProcessing: controlledProcessing = false,
  isSendDisabled = false,
  onAdd,
  onConfigureExtension,
  onExtensionMore,
  onMicrophone,
  onRemoveExtension,
  onSend,
  onStop,
  speechScopeKey,
  presentation = "default",
}: ChatAddInputProps) {
  const compactTextWidthRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pendingLayoutStateRef = useRef<ReturnType<
    typeof Flip.getState
  > | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isMultiline, setIsMultiline] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sendError, setSendError] = useState("");
  const [value, setValue] = useState(defaultValue);
  const hasContent = value.trim().length > 0;
  const hasExtensions = extensions.length > 0;
  const effectiveProcessing = isProcessing || controlledProcessing;
  const onTranscript = useCallback((text: string) => {
    setValue((draft) => mergeSpeechTranscript(draft, text));
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);
  const speech = useBrowserSpeechInput({ disabled: effectiveProcessing, onTranscript });
  const stopSpeech = speech.stop;
  useEffect(() => stopSpeech(), [stopSpeech, speechScopeKey]);
  const extensionSignature = extensions
    .map((extension) => `${extension.id}:${extension.summary ?? ""}`)
    .join("|");

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setValue(defaultValue));
    return () => window.cancelAnimationFrame(frame);
  }, [defaultValue]);

  const captureLayout = useCallback(() => {
    const root = rootRef.current;

    if (!root || prefersReducedMotion()) {
      pendingLayoutStateRef.current = null;
      return;
    }

    const targets = getLayoutTargets(root);
    Flip.killFlipsOf(targets);
    pendingLayoutStateRef.current = Flip.getState(targets);
  }, []);

  const measureMultiline = useCallback(() => {
    const input = inputRef.current;
    const root = rootRef.current;

    if (!input || input.value.length === 0) {
      if (isMultiline) {
        captureLayout();
        setIsMultiline(false);
      }
      return;
    }

    const previousHeight = input.style.height;
    const previousFlex = input.style.flex;
    const previousWidth = input.style.width;
    const canCaptureCompactWidth =
      !isMultiline &&
      root?.dataset.chatLayoutAnimating !== "true" &&
      (root?.getBoundingClientRect().width ?? 0) <= 641;
    if (canCaptureCompactWidth || compactTextWidthRef.current === null) {
      compactTextWidthRef.current = input.clientWidth;
    }
    const compactTextWidth = compactTextWidthRef.current;

    input.style.height = "22px";
    input.style.flex = "0 0 auto";
    if (compactTextWidth) {
      input.style.width = `${compactTextWidth}px`;
    }
    const singleLineHeight = input.clientHeight;
    const nextIsMultiline =
      input.value.includes("\n") || input.scrollHeight > singleLineHeight + 1;
    input.style.height = previousHeight;
    input.style.flex = previousFlex;
    input.style.width = previousWidth;

    if (nextIsMultiline !== isMultiline) {
      captureLayout();
      setIsMultiline(nextIsMultiline);
    }
  }, [captureLayout, isMultiline]);

  useEffect(() => {
    const frame = requestAnimationFrame(measureMultiline);
    return () => cancelAnimationFrame(frame);
  }, [measureMultiline, value]);

  useEffect(() => {
    const root = rootRef.current;
    const input = inputRef.current;
    if (
      !isMultiline &&
      input &&
      root &&
      root.dataset.chatLayoutAnimating !== "true" &&
      root.getBoundingClientRect().width <= 641
    ) {
      compactTextWidthRef.current = input.clientWidth;
    }
    const frame = requestAnimationFrame(measureMultiline);
    return () => cancelAnimationFrame(frame);
  }, [extensionSignature, isMultiline, measureMultiline]);

  useEffect(() => {
    const root = rootRef.current;

    if (!root || typeof ResizeObserver === "undefined") {
      return;
    }

    let previousWidth = root.getBoundingClientRect().width;
    const observer = new ResizeObserver(([entry]) => {
      const nextWidth = entry?.contentRect.width ?? previousWidth;

      if (nextWidth !== previousWidth) {
        previousWidth = nextWidth;
        measureMultiline();
      }
    });

    observer.observe(root);
    return () => observer.disconnect();
  }, [measureMultiline]);

  const state: ChatAddInputState = effectiveProcessing
    ? "进行中"
    : hasContent
      ? isMultiline
        ? "多行输入"
        : "单行输入"
      : isFocused
        ? "聚焦"
      : "未输入";

  const isMultiLineLayout = state === "多行输入";
  const extensionLayout = !hasExtensions
    ? "none"
    : isMultiLineLayout
      ? "expanded"
      : "inline";
  const isExpandedExtensionLayout = extensionLayout === "expanded";

  const extensionBar = hasExtensions && onRemoveExtension ? (
    <MerryChatExtensionBar
      extensions={extensions}
      isMoreOpen={isExtensionMoreOpen}
      onConfigure={onConfigureExtension}
      onMore={onExtensionMore}
      onRemove={onRemoveExtension}
    />
  ) : null;

  useGSAP(
    () => {
      const previousLayout = pendingLayoutStateRef.current;
      const root = rootRef.current;
      pendingLayoutStateRef.current = null;

      if (!root) {
        return;
      }

      if (!previousLayout || prefersReducedMotion()) {
        root.dataset.chatLayoutAnimating = "false";
        return;
      }

      root.dataset.chatLayoutAnimating = "true";
      Flip.from(previousLayout, {
        duration: layoutMotion.duration,
        ease: layoutMotion.ease,
        nested: true,
        prune: true,
        scale: false,
        onComplete: () => {
          root.dataset.chatLayoutAnimating = "false";
        },
        onInterrupt: () => {
          root.dataset.chatLayoutAnimating = "false";
        },
      });
    },
    {
      dependencies: [extensionLayout, isMultiLineLayout],
      scope: rootRef,
    },
  );

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  useEffect(() => {
    if (focusRequestKey > 0) focusInput();
  }, [focusInput, focusRequestKey]);

  const sendMessage = useCallback(async () => {
    const message = value.trim();

    if (!message || effectiveProcessing || isSendDisabled) {
      return;
    }

    const draftSnapshot = value;
    stopSpeech();

    if (isMultiline) {
      captureLayout();
    }

    setIsProcessing(true);
    setSendError("");
    setValue("");
    setIsMultiline(false);
    try {
      await onSend?.(message);
      if (onSend) setIsProcessing(false);
      focusInput();
    } catch (error) {
      setIsProcessing(false);
      setValue(draftSnapshot);
      setSendError(error instanceof ChatInputError ? error.message.trim() : "");
      focusInput();
    }
  }, [
    stopSpeech,
    captureLayout,
    focusInput,
    isMultiline,
    effectiveProcessing,
    isSendDisabled,
    onSend,
    value,
  ]);

  const stopProcessing = useCallback(() => {
    setIsProcessing(false);
    onStop?.();
    focusInput();
  }, [focusInput, onStop]);

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    setSendError("");
    setValue(event.currentTarget.value);
    measureMultiline();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <div
      aria-label="Merry 对话输入"
      className={cn(
        "merry-chat-input relative isolate grid w-full p-3",
        isExpandedExtensionLayout
          ? "h-[174px] grid-cols-[48px_minmax(0,1fr)_48px_6px_48px] grid-rows-[34px_56px_48px] gap-y-[6px]"
          : isMultiLineLayout
            ? "h-[128px] grid-cols-[48px_minmax(0,1fr)_48px_6px_48px] grid-rows-[56px_48px]"
          : "h-[72px] grid-cols-[48px_6px_minmax(0,1fr)_48px_6px_48px] grid-rows-[48px]",
        className,
      )}
      data-chat-add-state={state}
      data-chat-extension-layout={extensionLayout}
      data-figma-node-id={
        extensionLayout === "expanded"
          ? "571:59339"
          : extensionLayout === "inline"
            ? "571:61634"
            : figmaNodeIds[state]
      }
      data-chat-layout-motion="gsap-flip"
      ref={rootRef}
      role="group"
    >
      <span
        aria-hidden="true"
        className="merry-chat-input__glass pointer-events-none absolute inset-0 rounded-[32px]"
      />

      <AddButton
        className={
          isExpandedExtensionLayout
            ? "col-start-1 row-start-3"
            : isMultiLineLayout
              ? "col-start-1 row-start-2"
            : "col-start-1 row-start-1"
        }
        isOpen={isAddMenuOpen}
        isProcessing={effectiveProcessing}
        onAdd={onAdd}
      />
      {isExpandedExtensionLayout ? (
        <div
          className="relative z-10 col-[1/-1] row-start-1 min-w-0 px-[6px]"
          data-chat-layout-item=""
        >
          {extensionBar}
        </div>
      ) : null}
      <ChatTextInput
        extensionBar={extensionBar}
        extensionLayout={extensionLayout}
        inputRef={inputRef}
        isMultiline={isMultiLineLayout}
        isProcessing={effectiveProcessing}
        onBlur={() => setIsFocused(false)}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onKeyDown={handleKeyDown}
        state={state}
        value={value}
      />
      <MicrophoneButton
        presentation={presentation}
        className={
          isExpandedExtensionLayout
            ? "col-start-3 row-start-3"
            : isMultiLineLayout
              ? "col-start-3 row-start-2"
            : "col-start-4 row-start-1"
        }
        isProcessing={effectiveProcessing}
        isListening={speech.isListening}
        onMicrophone={onMicrophone ?? speech.toggle}
      />
      <SubmitButton
        className={
          isExpandedExtensionLayout
            ? "col-start-5 row-start-3"
            : isMultiLineLayout
              ? "col-start-5 row-start-2"
            : "col-start-6 row-start-1"
        }
        isSendDisabled={isSendDisabled}
        onSend={sendMessage}
        onStop={stopProcessing}
        state={state}
      />

      <span className="sr-only" role="status">
        当前状态：{state}
      </span>
      {sendError ? (
        <span
          className="absolute left-6 top-[calc(100%+8px)] z-20 text-sm text-[#d53f3f]"
          role="alert"
        >
          {sendError}
        </span>
      ) : null}
      {speech.message ? <span role="status" className="absolute bottom-[calc(100%+8px)] left-6 z-20 max-w-[calc(100%-48px)] rounded-xl bg-white/80 px-3 py-1 text-xs text-[#19262b]">{speech.message}</span> : null}
      <span
        aria-hidden="true"
        className="merry-chat-input__stroke pointer-events-none absolute inset-0 rounded-[32px]"
      />
    </div>
  );
}
